import * as React from 'react';
// @ts-expect-error - react-tooltip has no bundled types
import ReactTooltip from 'react-tooltip';

import {Button} from 'shells/button/button';
import {CardContentWithHeader} from 'shells/card/card-content-with-header';
import {TrashRegularIcon} from 'shells/icon/svg/trash-regular-icon';
import {Stack} from 'shells/layout/stack/stack';
import {TextField} from 'shells/textfield/textfield';
import {Toolbar} from 'shells/toolbar/toolbar';
import type {ToolbarOption} from 'shells/toolbar/toolbar';
import {Body} from 'shells/typography/body';

import {isValidUrlInput, VISITOR_IQ_URL_BLOCKLIST_INFO_TEXT} from './helpers';

import styles from './visitor-iq-url-blocklist-settings.module.css';

const URL_TOOLTIP_CHAR_THRESHOLD = 50;

type UrlRowProps = {
    url: string;
    disabled?: boolean;
    onRemove: () => void;
};

function VisitorIqBlocklistUrlRow({url, disabled, onRemove}: UrlRowProps) {
    const [isHovered, setIsHovered] = React.useState(false);

    const showToolbar = isHovered;
    const showUrlTooltip = url.length > URL_TOOLTIP_CHAR_THRESHOLD;

    React.useEffect(() => {
        ReactTooltip.rebuild();
    }, [url, showUrlTooltip]);

    const toolbarOptions = React.useMemo<ToolbarOption[]>(
        () => [
            {
                id: 'remove',
                icon: TrashRegularIcon,
                tooltipText: 'Remove',
                onClick: onRemove,
                isDisabled: disabled,
            },
        ],
        [disabled, onRemove]
    );

    return (
        <>
            <div
                className={styles['url-row']}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <div className={styles['url-row-text']} data-tip={showUrlTooltip ? url : undefined}>
                    <Body size='small' truncate={{type: 'inline'}} isFullWidth={true}>
                        {url}
                    </Body>
                </div>
                {showToolbar ? (
                    <div className={styles['url-row-toolbar']}>
                        <Toolbar options={toolbarOptions} />
                    </div>
                ) : null}
            </div>
        </>
    );
}

const MAX_URLS = 10;

type Props = {
    urls: string[];
    onChange: (urls: string[]) => void;
    disabled?: boolean;
};

export function VisitorIqUrlBlocklistSettings(props: Props) {
    const [draft, setDraft] = React.useState('');
    const [validationError, setValidationError] = React.useState<string | undefined>(undefined);

    const trimmedDraft = draft.trim();

    const handleAdd = () => {
        if (props.disabled) {
            return;
        }
        if (!trimmedDraft || props.urls.length >= MAX_URLS) {
            return;
        }
        if (props.urls.includes(trimmedDraft)) {
            return;
        }

        const urlValidationError = isValidUrlInput(trimmedDraft);
        if (urlValidationError !== null) {
            setValidationError(urlValidationError);

            return;
        }

        props.onChange([...props.urls, trimmedDraft]);
        setDraft('');
        setValidationError(undefined);
    };

    const handleDraftChange = (value: string) => {
        setDraft(value);
        if (validationError) {
            setValidationError(undefined);
        }
    };

    const handleRemove = (index: number) => {
        props.onChange(props.urls.filter((_, i) => i !== index));
    };

    const canAdd =
        trimmedDraft.length > 0 &&
        props.urls.length < MAX_URLS &&
        !props.urls.includes(trimmedDraft);

    return (
        <CardContentWithHeader
            fitContent={false}
            title='URL blocklist'
            tooltipInfo={<Body size='small'>{VISITOR_IQ_URL_BLOCKLIST_INFO_TEXT}</Body>}
            shouldTooltipFadeIn={true}
            subtitle='Exclude companies that have visited any of these URLs and their subpages. Maximum 10 URLs. Changes to the blocklist may take some time to process.'
            content={
                <div className={styles['url-blocklist-content']}>
                    <Stack isHorizontal={true} spacing={8} alignItems='start'>
                        <Stack spacing={4} isFullWidth={true}>
                            <TextField
                                placeholder='https://example.com/careers'
                                value={draft}
                                onChange={handleDraftChange}
                                onEnter={handleAdd}
                                hasError={Boolean(validationError)}
                                size='small'
                            />
                            {validationError ? (
                                <Body color='error' size='small'>
                                    {validationError}
                                </Body>
                            ) : null}
                        </Stack>
                        <Button
                            type='button'
                            variant='secondary'
                            onClick={handleAdd}
                            disabled={props.disabled || !canAdd}
                            size='big'
                        >
                            Add
                        </Button>
                    </Stack>
                    {props.urls.length > 0 ? (
                        <div className={styles['url-list']}>
                            {props.urls.map((url, index) => (
                                <VisitorIqBlocklistUrlRow
                                    key={`${url}-${index}`}
                                    url={url}
                                    disabled={props.disabled}
                                    onRemove={() => handleRemove(index)}
                                />
                            ))}
                        </div>
                    ) : null}
                </div>
            }
        />
    );
}
